import React from 'react';
import { useTranslation } from 'react-i18next';
import { KnowledgeDocument, KnowledgeProcessingStatus, KnowledgeVisibility } from '../types/Knowledge';
import {
  KnowledgeFileKind,
  formatDateTime,
  formatFileSize,
  getFileKind,
  getShareableUrl,
} from '../utils/knowledgeDocumentPresentation';
import styles from './KnowledgeDocumentCompactViews.module.css';

export type KnowledgeDocumentsViewMode = 'detailed' | 'grid' | 'list';

export const KNOWLEDGE_DOCUMENTS_VIEW_MODES: KnowledgeDocumentsViewMode[] = ['detailed', 'grid', 'list'];

export const isKnowledgeDocumentsViewMode = (value: unknown): value is KnowledgeDocumentsViewMode =>
  typeof value === 'string' && (KNOWLEDGE_DOCUMENTS_VIEW_MODES as string[]).includes(value);

const fileKindTone: Record<KnowledgeFileKind, string> = {
  pdf: styles.fileKindPdf,
  doc: styles.fileKindDoc,
  json: styles.fileKindJson,
  image: styles.fileKindImage,
  text: styles.fileKindText,
};

const MAX_TILE_TAGS = 3;

export interface KnowledgeDocumentCompactViewProps {
  documents: KnowledgeDocument[];
  busyDocumentId: string;
  getCategoryLabel: (fileCategory?: string | null) => string;
  /** Switches to the detailed view focused on this document, where editing and management actions live. */
  onOpenDetails: (document: KnowledgeDocument) => void;
  onCopyLink: (url: string) => void;
}

const useBadges = () => {
  const { t } = useTranslation(['knowledge']);

  const visibilityBadge = (value: KnowledgeVisibility) => {
    const label = value === 'customer_safe'
      ? t('customerSafe', { ns: 'knowledge' })
      : value === 'public'
        ? t('visibleToAll', { ns: 'knowledge' })
        : t('internalOnly', { ns: 'knowledge' });
    const tone = value === 'customer_safe'
      ? styles.badgeCustomerSafe
      : value === 'public'
        ? styles.badgePublic
        : styles.badgeInternal;
    return <span className={`${styles.badge} ${tone}`}>{label}</span>;
  };

  const statusBadge = (value: KnowledgeProcessingStatus) => {
    const tone = value === 'ready'
      ? styles.badgeReady
      : value === 'failed'
        ? styles.badgeFailed
        : styles.badgeProcessing;
    return <span className={`${styles.badge} ${tone}`}>{t(`status.${value}`, { ns: 'knowledge' })}</span>;
  };

  const restrictedBadge = (document: KnowledgeDocument) => (document.isAccessRestricted ? (
    <span className={`${styles.badge} ${styles.badgeRestricted}`} title={t('access.restrictedBadgeTitle', { ns: 'knowledge' })}>
      {t('access.restrictedBadge', { ns: 'knowledge' })}
    </span>
  ) : null);

  return { visibilityBadge, statusBadge, restrictedBadge };
};

interface RowActionsProps {
  document: KnowledgeDocument;
  busy: boolean;
  onOpenDetails: (document: KnowledgeDocument) => void;
  onCopyLink: (url: string) => void;
}

const RowActions: React.FC<RowActionsProps> = ({ document, busy, onOpenDetails, onCopyLink }) => {
  const { t } = useTranslation(['knowledge']);
  const url = getShareableUrl(document);

  return (
    <div className={styles.actions}>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className={`btn btn-secondary ${styles.actionButton}`}>
          {t('open', { ns: 'knowledge' })}
        </a>
      ) : null}
      {url ? (
        <button
          type="button"
          className={`btn btn-secondary ${styles.actionButton}`}
          onClick={() => onCopyLink(url)}
          disabled={busy}
        >
          {t('copyPublicUrl', { ns: 'knowledge' })}
        </button>
      ) : null}
      <button
        type="button"
        className={`btn btn-secondary ${styles.actionButton}`}
        onClick={() => onOpenDetails(document)}
        disabled={busy}
      >
        {t('view.details', { ns: 'knowledge' })}
      </button>
    </div>
  );
};

export const KnowledgeDocumentGrid: React.FC<KnowledgeDocumentCompactViewProps> = ({
  documents,
  busyDocumentId,
  getCategoryLabel,
  onOpenDetails,
  onCopyLink,
}) => {
  const { t } = useTranslation(['knowledge']);
  const { visibilityBadge, statusBadge, restrictedBadge } = useBadges();

  return (
    <div className={styles.grid} data-testid="knowledge-grid-view">
      {documents.map(document => {
        const fileKind = getFileKind(document);
        const busy = busyDocumentId === document.id;
        const hiddenTagCount = Math.max(0, document.tags.length - MAX_TILE_TAGS);

        return (
          <article key={document.id} className={`${styles.tile} ${busy ? styles.busy : ''}`}>
            <div className={styles.tileHead}>
              <div className={`${styles.fileKind} ${fileKindTone[fileKind.kind]}`} aria-hidden>
                {fileKind.label}
              </div>
              <div className={styles.tileBadges}>
                {statusBadge(document.processingStatus)}
                {restrictedBadge(document)}
              </div>
            </div>

            <button
              type="button"
              className={styles.tileTitle}
              onClick={() => onOpenDetails(document)}
              disabled={busy}
              title={document.title || document.originalFileName}
            >
              {document.title || document.originalFileName}
            </button>
            <p className={styles.tileMeta}>
              {document.originalFileName} · {formatFileSize(document.fileSize)}
            </p>

            <div className={styles.tileChips}>
              {visibilityBadge(document.visibility)}
              <span className={styles.categoryChip}>{getCategoryLabel(document.fileCategory)}</span>
            </div>

            {document.tags.length > 0 ? (
              <div className={styles.tagList}>
                {document.tags.slice(0, MAX_TILE_TAGS).map(tag => <span key={tag} className={styles.tag}>{tag}</span>)}
                {hiddenTagCount > 0 ? (
                  <span className={styles.tagMore}>{t('view.moreTags', { ns: 'knowledge', count: hiddenTagCount })}</span>
                ) : null}
              </div>
            ) : null}

            <div className={styles.tileFooter}>
              <span className={styles.tileUpdated}>
                {t('updated', { ns: 'knowledge' })} {formatDateTime(document.updatedAt)}
              </span>
              <RowActions document={document} busy={busy} onOpenDetails={onOpenDetails} onCopyLink={onCopyLink} />
            </div>
          </article>
        );
      })}
    </div>
  );
};

export const KnowledgeDocumentTable: React.FC<KnowledgeDocumentCompactViewProps> = ({
  documents,
  busyDocumentId,
  getCategoryLabel,
  onOpenDetails,
  onCopyLink,
}) => {
  const { t } = useTranslation(['knowledge']);
  const { visibilityBadge, statusBadge, restrictedBadge } = useBadges();

  return (
    <div className={styles.tableWrap} data-testid="knowledge-list-view">
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t('view.fileColumn', { ns: 'knowledge' })}</th>
            <th scope="col">{t('fileCategoryLabel', { ns: 'knowledge' })}</th>
            <th scope="col">{t('visibility', { ns: 'knowledge' })}</th>
            <th scope="col">{t('statusLabel', { ns: 'knowledge' })}</th>
            <th scope="col" className={styles.numericColumn}>{t('view.sizeColumn', { ns: 'knowledge' })}</th>
            <th scope="col">{t('updated', { ns: 'knowledge' })}</th>
            <th scope="col" className={styles.actionsColumn}>{t('actions', { ns: 'knowledge' })}</th>
          </tr>
        </thead>
        <tbody>
          {documents.map(document => {
            const fileKind = getFileKind(document);
            const busy = busyDocumentId === document.id;

            return (
              <tr key={document.id} className={busy ? styles.busy : undefined}>
                <td>
                  <div className={styles.fileCell}>
                    <div className={`${styles.fileKind} ${styles.fileKindSmall} ${fileKindTone[fileKind.kind]}`} aria-hidden>
                      {fileKind.label}
                    </div>
                    <div className={styles.fileCellText}>
                      <button
                        type="button"
                        className={styles.rowTitle}
                        onClick={() => onOpenDetails(document)}
                        disabled={busy}
                        title={document.title || document.originalFileName}
                      >
                        {document.title || document.originalFileName}
                      </button>
                      <span className={styles.rowMeta}>{document.originalFileName}</span>
                      {restrictedBadge(document)}
                    </div>
                  </div>
                </td>
                <td>{getCategoryLabel(document.fileCategory)}</td>
                <td>{visibilityBadge(document.visibility)}</td>
                <td>{statusBadge(document.processingStatus)}</td>
                <td className={styles.numericColumn}>{formatFileSize(document.fileSize)}</td>
                <td className={styles.nowrap}>{formatDateTime(document.updatedAt)}</td>
                <td className={styles.actionsColumn}>
                  <RowActions document={document} busy={busy} onOpenDetails={onOpenDetails} onCopyLink={onCopyLink} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export interface KnowledgeDocumentsViewToggleProps {
  value: KnowledgeDocumentsViewMode;
  onChange: (mode: KnowledgeDocumentsViewMode) => void;
}

export const KnowledgeDocumentsViewToggle: React.FC<KnowledgeDocumentsViewToggleProps> = ({ value, onChange }) => {
  const { t } = useTranslation(['knowledge']);

  return (
    <div className={styles.viewToggle} role="group" aria-label={t('view.label', { ns: 'knowledge' })}>
      {KNOWLEDGE_DOCUMENTS_VIEW_MODES.map(mode => (
        <button
          key={mode}
          type="button"
          className={`${styles.viewToggleButton} ${value === mode ? styles.viewToggleButtonActive : ''}`}
          aria-pressed={value === mode}
          onClick={() => onChange(mode)}
          data-testid={`knowledge-view-${mode}`}
        >
          {t(`view.${mode}`, { ns: 'knowledge' })}
        </button>
      ))}
    </div>
  );
};
